/* BSP-Raumbaum für die Rekonstruktion lesen: Ebenen, Knoten, Blätter, Flächen und Modelle (Hull 0) samt Entity-Lump.
 * Eigenständig und ohne DOM. Für die Darstellung gibt es bsp-parse.ts. */
import type { Vec3 } from '../model/types.ts';
import I18n from '../i18n.ts';

const _ = I18n.t;

/** Inhalte der Blätter (Raumbaum) */
export const CONTENTS = { EMPTY: -1, SOLID: -2, WATER: -3, SLIME: -4, LAVA: -5, SKY: -6, ORIGIN: -7, CLIP: -8 };

export interface TreePlane { n: Vec3; d: number }
export interface TreeNode { plane: number; ch: [number, number] }
export interface TreeTexInfo { s: Vec3; so: number; t: Vec3; to: number; tex: number; flags: number }
export interface TreeFace { plane: number; side: number; first: number; num: number; info: number }
export interface TreeModel { min: Vec3; max: Vec3; origin: Vec3; head: number; first: number; num: number }
export interface BspTree {
  entText: string; planes: TreePlane[]; miptex: string[]; verts: Vec3[]; nodes: TreeNode[]; texinfo: TreeTexInfo[];
  faces: TreeFace[]; leafs: { contents: number }[]; edges: [number, number][]; surf: number[]; models: TreeModel[];
}
/** Entity aus dem Entity-Lump; order merkt die Reihenfolge der Schlüssel */
export interface RawEntity { kv: Record<string, string>; order: string[] }

  export function readBspTree(buf: ArrayBuffer): BspTree {
    const dv = new DataView(buf), u8 = new Uint8Array(buf);
    if (buf.byteLength < 4 + 15 * 8) throw new Error(_('bsp.e_short'));
    const version = dv.getInt32(0, true);
    if (version !== 30) throw new Error(_('decomp.e_version', { ver: version }));
    const lump = (i: number) => ({ ofs: dv.getInt32(4 + i * 8, true), len: dv.getInt32(8 + i * 8, true) });
    const b: BspTree = { entText: '', planes: [], miptex: [], verts: [], nodes: [], texinfo: [], faces: [], leafs: [], edges: [], surf: [], models: [] };
    const E = lump(0); let s = '';
    for (let i = 0; i < E.len && u8[E.ofs + i]; i++) s += String.fromCharCode(u8[E.ofs + i]);
    b.entText = s;
    const P = lump(1); b.planes = [];
    for (let i = 0; i < P.len / 20; i++) { const o = P.ofs + i * 20; b.planes.push({ n: [dv.getFloat32(o, true), dv.getFloat32(o + 4, true), dv.getFloat32(o + 8, true)], d: dv.getFloat32(o + 12, true) }); }
    const T = lump(2); b.miptex = [];
    if (T.len >= 4) {
      const cnt = dv.getInt32(T.ofs, true);
      for (let i = 0; i < cnt; i++) {
        const off = dv.getInt32(T.ofs + 4 + i * 4, true);
        if (off <= 0 || T.ofs + off + 40 > buf.byteLength) { b.miptex.push(''); continue; }
        let n = ''; for (let k = 0; k < 16 && u8[T.ofs + off + k]; k++) n += String.fromCharCode(u8[T.ofs + off + k]);
        b.miptex.push(n);
      }
    }
    const V = lump(3); b.verts = [];
    for (let i = 0; i < V.len / 12; i++) b.verts.push([dv.getFloat32(V.ofs + i * 12, true), dv.getFloat32(V.ofs + i * 12 + 4, true), dv.getFloat32(V.ofs + i * 12 + 8, true)]);
    const N = lump(5); b.nodes = [];
    for (let i = 0; i < N.len / 24; i++) { const o = N.ofs + i * 24; b.nodes.push({ plane: dv.getInt32(o, true), ch: [dv.getInt16(o + 4, true), dv.getInt16(o + 6, true)] }); }
    const TI = lump(6); b.texinfo = [];
    for (let i = 0; i < TI.len / 40; i++) {
      const o = TI.ofs + i * 40, f = (k: number) => dv.getFloat32(o + k * 4, true);
      b.texinfo.push({ s: [f(0), f(1), f(2)], so: f(3), t: [f(4), f(5), f(6)], to: f(7), tex: dv.getInt32(o + 32, true), flags: dv.getInt32(o + 36, true) });
    }
    const F = lump(7); b.faces = [];
    for (let i = 0; i < F.len / 20; i++) { const o = F.ofs + i * 20; b.faces.push({ plane: dv.getUint16(o, true), side: dv.getUint16(o + 2, true), first: dv.getInt32(o + 4, true), num: dv.getInt16(o + 8, true), info: dv.getInt16(o + 10, true) }); }
    const LF = lump(10); b.leafs = [];
    for (let i = 0; i < LF.len / 28; i++) b.leafs.push({ contents: dv.getInt32(LF.ofs + i * 28, true) });
    const ED = lump(12); b.edges = [];
    for (let i = 0; i < ED.len / 4; i++) b.edges.push([dv.getUint16(ED.ofs + i * 4, true), dv.getUint16(ED.ofs + i * 4 + 2, true)]);
    const SE = lump(13); b.surf = [];
    for (let i = 0; i < SE.len / 4; i++) b.surf.push(dv.getInt32(SE.ofs + i * 4, true));
    const M = lump(14); b.models = [];
    for (let i = 0; i < M.len / 64; i++) {
      const o = M.ofs + i * 64, f = (k: number) => dv.getFloat32(o + k * 4, true);
      b.models.push({ min: [f(0), f(1), f(2)], max: [f(3), f(4), f(5)], origin: [f(6), f(7), f(8)], head: dv.getInt32(o + 36, true), first: dv.getInt32(o + 56, true), num: dv.getInt32(o + 60, true) });
    }
    return b;
  }

  export function parseEntities(text: string): RawEntity[] {
    const ents: RawEntity[] = []; let cur: RawEntity | null = null;
    text.split(/\r?\n/).forEach((ln) => {
      ln = ln.trim();
      if (ln === '{') cur = { kv: {}, order: [] };
      else if (ln === '}') { if (cur) ents.push(cur); cur = null; }
      else if (cur) { const m = /^"([^"]*)"\s+"([^"]*)"/.exec(ln); if (m) { if (!(m[1] in cur.kv)) cur.order.push(m[1]); cur.kv[m[1]] = m[2]; } }
    });
    return ents;
  }

